import useSWR from 'swr';

import { fetchEndpointData } from '@/utils/fetch.client';

// ── Types ────────────────────────────────────────────────────────────

/**
 * Where a skill lives. `personal` and `team` are both real teams — every user has a personal team,
 * the same trick playbooks use — while `system` is the staff-managed tier every space sees
 * read-only.
 */
export type SkillScope = 'personal' | 'team' | 'system';

export interface Skill {
  id: string;
  /** `null` on system skills. */
  team_id: string | null;
  scope: SkillScope;
  name: string;
  description: string;
  /** For a system skill on the `available` list, the effective state for this team. */
  enabled: boolean;
  revision: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** A row from `available`: everything a session in this team could reach, and why it might not. */
export interface AvailableSkill extends Skill {
  /**
   * Set when a skill of the same name at a higher-precedence scope wins. Without this a user's own
   * skill can be silently overridden and there is nothing on screen to explain it.
   */
  shadowed_by: SkillScope | null;
  read_only: boolean;
}

export interface SkillFile {
  path: string;
  size_bytes: number;
  is_executable: boolean;
  is_binary: boolean;
  updated_at: string;
}

export interface SkillDetail extends Skill {
  /** The `SKILL.md` source. Its frontmatter is what defines `name` and `description`. */
  skill_md: string;
  files: SkillFile[];
}

/** `name` is only consulted when the frontmatter omits one. */
export interface SkillInput {
  skill_md: string;
  name?: string;
  enabled?: boolean;
}

interface AvailableSkillsResponse {
  skills: AvailableSkill[];
}
interface ListSkillsResponse {
  skills: Skill[];
}
interface SkillResponse {
  skill: SkillDetail;
}

// ── SWR Hooks ──────────────────────────────────────────────────────────

/**
 * Every skill a session in this team would see, at all three scopes, each tagged with its scope,
 * effective enabled state, and whether something shadows it.
 *
 * One call rather than one per scope: the question the UI is really answering is "what does my
 * agent actually have?", and three separate lists cannot answer it without recombining them on the
 * client — including the precedence rule, which belongs in one place.
 */
export function useAvailableSkills(teamId: string | null) {
  const key = teamId ? `/api/v1/teams/${teamId}/skills/available` : null;
  const { data, mutate, isLoading } = useSWR(key, () =>
    fetchEndpointData<AvailableSkillsResponse>(`/api/v1/teams/${teamId}/skills/available`),
  );
  return { skills: data?.skills ?? [], mutate, isLoading };
}

/** One skill with its `SKILL.md` and file list. `null` id skips the fetch. */
export function useSkill(teamId: string | null, skillId: string | null) {
  const key = teamId && skillId ? `/api/v1/teams/${teamId}/skills/${skillId}` : null;
  const { data, mutate, isLoading } = useSWR(key, () =>
    fetchEndpointData<SkillResponse>(`/api/v1/teams/${teamId}/skills/${skillId}`),
  );
  return { skill: data?.skill ?? null, mutate, isLoading };
}

export function useAdminSkills() {
  const { data, mutate, isLoading } = useSWR('/api/v1/admin/skills', () =>
    fetchEndpointData<ListSkillsResponse>('/api/v1/admin/skills'),
  );
  return { skills: data?.skills ?? [], mutate, isLoading };
}

export function useAdminSkill(skillId: string | null) {
  const key = skillId ? `/api/v1/admin/skills/${skillId}` : null;
  const { data, mutate, isLoading } = useSWR(key, () =>
    fetchEndpointData<SkillResponse>(`/api/v1/admin/skills/${skillId}`),
  );
  return { skill: data?.skill ?? null, mutate, isLoading };
}

// ── Action Functions ───────────────────────────────────────────────────

export async function createSkill(teamId: string, body: SkillInput): Promise<SkillDetail> {
  const res = await fetchEndpointData<SkillResponse>(`/api/v1/teams/${teamId}/skills`, { method: 'POST', body });
  return res.skill;
}

export async function updateSkill(teamId: string, skillId: string, body: SkillInput): Promise<SkillDetail> {
  const res = await fetchEndpointData<SkillResponse>(`/api/v1/teams/${teamId}/skills/${skillId}`, {
    method: 'PUT',
    body,
  });
  return res.skill;
}

export async function deleteSkill(teamId: string, skillId: string) {
  return fetchEndpointData(`/api/v1/teams/${teamId}/skills/${skillId}`, { method: 'DELETE' });
}

/** Switch a system skill on or off for this team. Team admins only, server-side. */
export async function setSystemSkillEnabled(teamId: string, skillId: string, enabled: boolean) {
  return fetchEndpointData(`/api/v1/teams/${teamId}/skills/system/${skillId}`, {
    method: 'PUT',
    body: { enabled },
  });
}

/**
 * Copy a system skill into this team as an editable one. The copy arrives disabled — the original
 * is still on, and two live skills of the same name would mean the fork silently shadows it before
 * anyone has looked at the copy.
 */
export async function forkSystemSkill(teamId: string, skillId: string): Promise<SkillDetail> {
  const res = await fetchEndpointData<SkillResponse>(`/api/v1/teams/${teamId}/skills/${skillId}/fork`, {
    method: 'POST',
  });
  return res.skill;
}

// ── Bundled files ──────────────────────────────────────────────────────

/** One file's contents. Text when the bytes are valid UTF-8, base64 otherwise. */
export interface SkillFileContent {
  path: string;
  is_executable: boolean;
  content?: string;
  content_base64?: string;
}

export async function getSkillFile(teamId: string, skillId: string, path: string): Promise<SkillFileContent> {
  return fetchEndpointData<SkillFileContent>(`/api/v1/teams/${teamId}/skills/${skillId}/files/${path}`);
}

export async function putSkillFile(teamId: string, skillId: string, path: string, content: string) {
  return fetchEndpointData(`/api/v1/teams/${teamId}/skills/${skillId}/files/${path}`, {
    method: 'PUT',
    body: { content },
  });
}

export async function deleteSkillFile(teamId: string, skillId: string, path: string) {
  return fetchEndpointData(`/api/v1/teams/${teamId}/skills/${skillId}/files/${path}`, { method: 'DELETE' });
}

// ── Archives ───────────────────────────────────────────────────────────

/**
 * Upload a `.zip` as a new skill.
 *
 * Uses `fetch` directly rather than {@link fetchEndpointData}, which always JSON-stringifies the
 * body and sets a JSON content type — a `FormData` sent through it would arrive as `"[object
 * FormData]"`. The browser must set the multipart boundary itself, so no `Content-Type` is given.
 */
export async function importSkillZip(teamId: string, file: File): Promise<SkillDetail> {
  return uploadZip(`/api/v1/teams/${teamId}/skills/import`, 'POST', file);
}

/** Replace an existing skill's whole file set from a `.zip`. */
export async function importOverSkillZip(teamId: string, skillId: string, file: File): Promise<SkillDetail> {
  return uploadZip(`/api/v1/teams/${teamId}/skills/${skillId}/import`, 'PUT', file);
}

export async function importAdminSkillZip(file: File): Promise<SkillDetail> {
  return uploadZip('/api/v1/admin/skills/import', 'POST', file);
}

export async function importOverAdminSkillZip(skillId: string, file: File): Promise<SkillDetail> {
  return uploadZip(`/api/v1/admin/skills/${skillId}/import`, 'PUT', file);
}

async function uploadZip(url: string, method: 'POST' | 'PUT', file: File): Promise<SkillDetail> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(url, { method, body: form, credentials: 'same-origin' });
  if (!res.ok) {
    // The API puts the actionable reason in `details` — "your zip is wrong" is only useful with it.
    const body = await res.json().catch(() => null);
    throw new Error(body?.details ?? body?.error ?? `Upload failed (${res.status})`);
  }
  return (await res.json()).skill;
}

/** Export is a plain download; an anchor beats fetching a blob and revoking an object URL. */
export function skillExportHref(teamId: string, skillId: string): string {
  return `/api/v1/teams/${teamId}/skills/${skillId}/export`;
}

export function adminSkillExportHref(skillId: string): string {
  return `/api/v1/admin/skills/${skillId}/export`;
}

// ── Admin actions ──────────────────────────────────────────────────────

export async function createAdminSkill(body: SkillInput): Promise<SkillDetail> {
  const res = await fetchEndpointData<SkillResponse>('/api/v1/admin/skills', { method: 'POST', body });
  return res.skill;
}

export async function updateAdminSkill(skillId: string, body: SkillInput): Promise<SkillDetail> {
  const res = await fetchEndpointData<SkillResponse>(`/api/v1/admin/skills/${skillId}`, { method: 'PUT', body });
  return res.skill;
}

export async function deleteAdminSkill(skillId: string) {
  return fetchEndpointData(`/api/v1/admin/skills/${skillId}`, { method: 'DELETE' });
}

export async function getAdminSkillFile(skillId: string, path: string): Promise<SkillFileContent> {
  return fetchEndpointData<SkillFileContent>(`/api/v1/admin/skills/${skillId}/files/${path}`);
}

export async function putAdminSkillFile(skillId: string, path: string, content: string) {
  return fetchEndpointData(`/api/v1/admin/skills/${skillId}/files/${path}`, {
    method: 'PUT',
    body: { content },
  });
}

export async function deleteAdminSkillFile(skillId: string, path: string) {
  return fetchEndpointData(`/api/v1/admin/skills/${skillId}/files/${path}`, { method: 'DELETE' });
}

// ── SKILL.md frontmatter ───────────────────────────────────────────────

export interface Frontmatter {
  name: string | null;
  description: string | null;
  /**
   * False when a field can't be edited through the form — a block scalar, a multi-line value, or
   * no frontmatter fence at all. The editor falls back to raw mode rather than silently mangling
   * something it doesn't understand.
   */
  editable: boolean;
}

export interface ParsedSkillMd {
  frontmatter: Frontmatter;
  body: string;
}

/** Matches the backend's cap, which is what actually rejects an over-long description. */
export const MAX_DESCRIPTION_LEN = 1024;

const FENCE = '---';

/**
 * Split a `SKILL.md` into frontmatter fields and body.
 *
 * Deliberately line-based and dependency-free: `package.json` has no YAML parser and two scalar
 * fields do not justify adding one. Anything this can't read confidently sets `editable: false`,
 * which the editor honours by showing raw text — the same leniency the backend parser applies.
 */
export function parseFrontmatter(source: string): ParsedSkillMd | null {
  const lines = source.split('\n');
  if (lines[0]?.trim() !== FENCE) return null;

  const closing = lines.findIndex((line, i) => i > 0 && line.trim() === FENCE);
  if (closing === -1) return null;

  const fm = lines.slice(1, closing);
  const body = lines.slice(closing + 1).join('\n');

  const read = (key: string): { value: string | null; simple: boolean } => {
    const line = fm.find((candidate) => candidate.startsWith(`${key}:`));
    if (line === undefined) return { value: null, simple: true };
    const raw = line.slice(key.length + 1).trim();
    // A block scalar (`|`, `>`) or an empty value continues onto following lines; rewriting just
    // this line would corrupt it.
    if (raw === '' || raw.startsWith('|') || raw.startsWith('>')) {
      return { value: null, simple: false };
    }
    return { value: unquote(raw), simple: true };
  };

  const name = read('name');
  const description = read('description');

  return {
    frontmatter: {
      name: name.value,
      description: description.value,
      editable: name.simple && description.simple,
    },
    body,
  };
}

function unquote(value: string): string {
  if (
    value.length >= 2 &&
    ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'")))
  ) {
    return value.slice(1, -1);
  }
  return value;
}

/**
 * Rewrite exactly one frontmatter line, leaving everything else byte-identical.
 *
 * Not a reserialization: `allowed-tools`, `metadata`, `license` and any key we don't model must
 * survive a round trip through the form untouched, and re-emitting the document from a parsed
 * model would quietly drop or reorder them.
 */
export function setFrontmatterField(source: string, key: 'name' | 'description', value: string): string {
  const lines = source.split('\n');
  if (lines[0]?.trim() !== FENCE) return source;
  const closing = lines.findIndex((line, i) => i > 0 && line.trim() === FENCE);
  if (closing === -1) return source;

  const encoded = needsQuoting(value) ? JSON.stringify(value) : value;
  const next = `${key}: ${encoded}`;

  const index = lines.findIndex((line, i) => i > 0 && i < closing && line.startsWith(`${key}:`));
  if (index === -1) {
    lines.splice(closing, 0, next);
  } else {
    lines[index] = next;
  }
  return lines.join('\n');
}

/** YAML scalars that would be misread unquoted: leading indicators, colons, or a trailing space. */
function needsQuoting(value: string): boolean {
  if (value === '') return true;
  if (/^[-?:,[\]{}#&*!|>'"%@`]/.test(value)) return true;
  if (value.includes(': ') || value.endsWith(':')) return true;
  if (value !== value.trim()) return true;
  return false;
}

/** A minimal, valid `SKILL.md` for a newly created skill. */
export function seedSkillMd(name: string, description: string): string {
  return [
    FENCE,
    `name: ${needsQuoting(name) ? JSON.stringify(name) : name}`,
    `description: ${needsQuoting(description) ? JSON.stringify(description) : description}`,
    FENCE,
    '',
    `# ${name}`,
    '',
    'Describe the procedure step by step. Keep it short — the agent already knows the basics.',
    '',
  ].join('\n');
}

/** Mirrors the backend's `is_valid_skill_name`. */
export function isValidSkillName(name: string): boolean {
  return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(name) && name.length <= 64;
}

export const SCOPE_LABELS: Record<SkillScope, string> = {
  personal: 'Personal',
  team: 'Team',
  system: 'System',
};
